import React, { useEffect } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import config from 'config';

import { onLCP, onFID, onCLS, onINP, onTTFB, onFCP } from 'web-vitals';
import collect from '@wsc/utils/collect';

const GA_PROPERTY_ID = 'G-7G5JFZB54R';

const analyticsOptionsSelector = createSelector(
	[(state) => state?.componentConfig],
	(componentConfig) => {
		const { analyticsOptions, loaded } = componentConfig || {};
		return { analyticsOptions, loaded };
	},
);

/**
 * @module PerformanceMonitor
 *
 * @description
 * Module to track and report web-vitals for stores.
 *
 * ## Uses
 * - [`Web Vitals`]{@link https://www.npmjs.com/package/web-vitals} package for collecting the metrics.
 * - Uses [`GA4`]{@link https://developers.google.com/analytics/devguides/collection/ga4} for reporting.
 *
 * ## Configuration
 *
 * ### Web Vitals
 * -  onLCP, onFID, onCLS, onINP, onTTFB functions from web-vitals are used for collecting respective metrics
 *
 * ### Google Analytics 4
 * - A standalone GA4 property is configured for collecting the metrics.
 * - This GA4 property only collects performance metrics.
 * - Property uses a custom datalayer array `window.dataLayer2` which allows it to not collect other events and prevents performance events from being reported to other GA properties on the page.
 * - The custom property is defined in [`setWindowGTAG`]{@link module:PerformanceMonitor~setWindowGTAG}.
 * - To make GA4 use the custom property,the value is passed to GA URL as a parameter `l=<myCustomDataLayerName>`
 *
 *
 */
const PerformanceMonitor = ({ storeId }) => {
	const { loaded, analyticsOptions } = useSelector(analyticsOptionsSelector);
	const { analyticsEnabled = true } = analyticsOptions || {};
	/**
	 * @function setWindowGTAG
	 */
	const setWindowGTAG = () => {
		window.dataLayer2 = window.dataLayer2 || [];
		function gtag() {
			window.dataLayer2.push(arguments);
		}
		gtag('js', new Date());
		window.perfTag = gtag;
	};

	const reportMetric = (metric) => {
		window.perfTag('event', metric.name, metric);
		if ('entries' in metric) delete metric.entries;
		collect(metric);
	};

	const loadGAIDs = (env) => {
		window.perfTag('config', GA_PROPERTY_ID, {
			page_location: window.location.href,
			env,
			storeId,
			version: 2,
			cookie_flags: 'SameSite=None;Secure',
			cookie_path: window.location.pathname,
		});
	};

	const onScriptLoaded = (env) => {
		setWindowGTAG();
		loadGAIDs(env);
		initiateListeners();
	};

	const load = (env) => {
		const gaSrc = `https://www.googletagmanager.com/gtag/js?id=${GA_PROPERTY_ID}&l=dataLayer2`;
		const gaScript = document.createElement('script');
		gaScript.src = gaSrc;
		gaScript.async = true;
		gaScript.onload = () => onScriptLoaded(env);
		document.head.appendChild(gaScript);
	};

	const prepareLCP = (metric) => {
		const toReport = { ...metric };

		if (toReport?.entries && toReport.entries.length > 0) {
			const topEntry = toReport.entries[0];
			const entryInfo = {
				url: topEntry?.url,
				loadTime: topEntry?.loadTime,
				startTime: topEntry?.startTime,
				duration: topEntry?.duration,
				renderTime: topEntry?.renderTime,
				size: topEntry?.size,
				tagName: topEntry?.element?.tagName,
			};
			Object.assign(toReport, entryInfo);
			delete toReport.entries;
		}

		reportMetric(toReport);
	};

	const initiateListeners = () => {
		onLCP(prepareLCP);
		onFID(reportMetric);
		onCLS(reportMetric);
		onINP(reportMetric);
		onTTFB(reportMetric);
		onFCP(reportMetric);
	};

	useEffect(() => {
		if (!loaded) return;
		if (analyticsEnabled) {
			load(config.ENV);
		}
	}, [loaded, analyticsEnabled]);

	return <></>;
};

export default PerformanceMonitor;
